// src/lib/marksMath.js
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  New. The pure half of the manager's morning: who warmed up,
//               who is due recognition, what to call out from yesterday, and
//               where each person sits in the thirteen steps. Queries live in
//               recognitions.js; this file is arithmetic so node can test it.
//
// EVERYTHING HERE RETURNS PEOPLE OR MOMENTS, NEVER A RANKING.
//
// There is no function in this file that sorts people by how many marks they
// have, how far through the steps they are, or how many drills they ran. That
// is deliberate and it is the whole design: the research on leaderboards is
// that they harm everybody below the middle, and Jazz's own rhythm asks for
// recognition of behaviour rather than a table of it. People come back in the
// order they were given, which is the order of the floor.
//
// Voice rules: no Oxford commas. No em dashes. Real apostrophes.

import { dayKey, addDays } from './localDate.js'

const DAY_MS = 24 * 60 * 60 * 1000

// WHO HAS NOT BEEN RECOGNISED IN A WEEK.
//
// Gallup: recognise every employee at least once every seven days, and only
// one in three workers say they were in the last week. This is the quiet line
// on the MANAGER's screen that makes the second number better. It is never
// shown to the floor and it never says anything about the person, only about
// whether their manager has noticed them lately.
export function dueForRecognition(people, recognitions, now = new Date(), days = 7) {
  const cutoff = now.getTime() - days * DAY_MS
  const recent = new Set(
    (recognitions || [])
      .filter((r) => new Date(r.created_at).getTime() >= cutoff)
      .map((r) => r.given_to)
  )
  return (people || []).filter((p) => !recent.has(p.id))
}

// The marks this person has not opened yet, newest first. The moment on their
// Today shows these once, then they are stamped seen.
export function unseenFor(userId, recognitions) {
  return (recognitions || [])
    .filter((r) => r.given_to === userId && !r.seen_at)
    .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))
}

// WHAT TO CALL OUT THIS MORNING. A good meeting opens by recognising
// yesterday, and the manager should not have to remember what they gave at
// four in the afternoon. Local days, because a mark given at 9pm Central is
// yesterday on the floor and today in UTC.
export function yesterdaysMarks(recognitions, today = dayKey(new Date())) {
  const y = addDays(today, -1)
  return (recognitions || []).filter((r) => dayKey(new Date(r.created_at)) === y)
}

// Marks given today, for the recap after the meeting.
export function todaysMarks(recognitions, today = dayKey(new Date())) {
  return (recognitions || []).filter((r) => dayKey(new Date(r.created_at)) === today)
}

// HOW EACH PERSON'S MORNING WENT, from their morning_routine events.
//
// Returns one entry per person, in the order given, with `warmedUp` false and
// the rest null for anybody who has not done the routine today. Null means we
// do not know, which is a different fact from zero walked, and the screen says
// different things for each.
export function morningFor(people, events, today = dayKey(new Date())) {
  const todays = (events || []).filter(
    (e) => e.kind === 'morning_routine' && dayKey(new Date(e.created_at)) === today
  )
  const byUser = new Map()
  for (const e of todays) byUser.set(e.user_id, e) // last one wins, there is normally one
  return (people || []).map((p) => {
    const e = byUser.get(p.id)
    if (!e) return { id: p.id, warmedUp: false, unsold: null, kept: null }
    const m = e.metadata || {}
    return {
      id: p.id,
      warmedUp: true,
      unsold: Number.isInteger(m.unsold) ? m.unsold : null,
      kept: m.kept === true ? true : m.kept === false ? false : null,
    }
  })
}

// WHERE EACH PERSON IS IN THE THIRTEEN STEPS: the lowest step they have not
// passed. Passed means check_passed or signed_off, the same rule the rest of
// the product uses. Somebody through all thirteen sits at 14, which the
// render shows as finished rather than as a fourteenth step.
export function stageFor(personId, progressRows) {
  const passed = new Set(
    (progressRows || [])
      .filter((r) => r.user_id === personId && (r.status === 'check_passed' || r.status === 'signed_off'))
      .map((r) => r.steps?.number ?? r.step_number)
      .filter((n) => Number.isInteger(n))
  )
  for (let n = 1; n <= 13; n += 1) if (!passed.has(n)) return n
  return 14
}

// HAS THIS PERSON MOVED THIS WEEK. The track shows position, and position alone
// reads as a ranking the moment two faces sit on different steps. Movement is
// the antidote: somebody on Step 2 who moved yesterday is doing better than
// somebody on Step 9 who has not moved in a fortnight, and this is what lets
// the screen say so.
export function movedWithin(personId, progressRows, now = new Date(), days = 7) {
  const cutoff = now.getTime() - days * DAY_MS
  return (progressRows || []).some(
    (r) =>
      r.user_id === personId &&
      (r.status === 'check_passed' || r.status === 'signed_off') &&
      r.updated_at &&
      new Date(r.updated_at).getTime() >= cutoff
  )
}

// The one line on the manager's screen above the faces. Counts of people,
// never names of people who did not do something.
export function floorLine(morning) {
  const total = morning.length
  if (total === 0) return 'Nobody on the floor yet.'
  const warm = morning.filter((m) => m.warmedUp).length
  const walked = morning.reduce((n, m) => n + (m.unsold || 0), 0)
  // The routine's top choice is "5+", so anybody on it makes the sum a floor
  // rather than a count, and the line says so.
  const open = morning.some((m) => m.unsold >= 5)
  const parts = [`${warm} of ${total} warmed up`]
  if (walked > 0) parts.push(`${walked}${open ? '+' : ''} walked yesterday`)
  return parts.join(', ')
}

// "Today", "Yesterday", or the weekday, for a moment on the floor. Past a week
// it is the date, because "Tuesday" nine days ago is a different Tuesday.
export function dayLabel(iso, today = dayKey(new Date())) {
  const d = new Date(iso)
  const key = dayKey(d)
  if (key === today) return 'today'
  if (key === addDays(today, -1)) return 'yesterday'
  if (key > addDays(today, -7)) return d.toLocaleDateString('en-US', { weekday: 'long' })
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// The most recent mark a person received, or null. For the line at the top of
// the give-a-mark sheet, which tells a manager what they last noticed before
// they notice something else.
export function lastMarkFor(personId, recognitions) {
  let best = null
  for (const r of recognitions || []) {
    if (r.given_to !== personId) continue
    if (!best || String(r.created_at) > String(best.created_at)) best = r
  }
  return best
}

// Moments grouped by day, newest day first, keeping each day's own order. The
// floor-this-morning list reads "today", then "yesterday" (which is what gets
// called out at the meeting), then the rest of the week.
export function groupByDay(recognitions, today = dayKey(new Date())) {
  const groups = []
  const at = new Map()
  const ordered = [...(recognitions || [])].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))
  for (const r of ordered) {
    const label = dayLabel(r.created_at, today)
    if (!at.has(label)) {
      at.set(label, groups.length)
      groups.push({ label, items: [] })
    }
    groups[at.get(label)].items.push(r)
  }
  return groups
}

// Names the way a person says them, with no Oxford comma: "Dale", "Dale and
// Kim", "Dale, Kim and Ray". Past `max` it stops listing: "Dale, Kim and 3
// more", because a line of eight names is a register, not a sentence.
export function joinNames(names, max = 3) {
  const list = (names || []).filter(Boolean)
  if (list.length === 0) return ''
  if (list.length > max) return `${list.slice(0, max - 1).join(', ')} and ${list.length - (max - 1)} more`
  if (list.length === 1) return list[0]
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`
}
